import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useBrandingContext } from '../../contexts/BrandingContext'
import { useCart } from '../../contexts/CartContext'
import { useCompanyInfo } from '../../hooks/useBranding'
import { useThemeColors } from '../../hooks/useThemeColors'
import { useSubscriptionFeatures } from '../../hooks/useSubscriptionFeatures'
import { useOrganization } from '../../contexts/OrganizationContext'
import { CartSidebar } from '../cart/CartSidebar'
import { ShoppingCartIcon } from '@heroicons/react/24/outline'
import { OrganizationSwitcher } from '../OrganizationSwitcher'
import { OrganizationBadge } from '../OrganizationBadge'

export default function TopBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, profile, signOut, isAdmin, isStaff, isSuperAdmin, organizationRole } = useAuth()
  const { branding } = useBrandingContext()
  const { itemCount } = useCart()
  const companyInfo = useCompanyInfo()
  const themeColors = useThemeColors()
  const { isTrainerMode, getTerminology } = useSubscriptionFeatures()
  const { organization } = useOrganization()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [showCart, setShowCart] = useState(false)
  
  // Use organizationRole from organization_users table as source of truth
  const isMember = organizationRole === 'member' || (!isAdmin && !isStaff && !organizationRole)
  const terminology = getTerminology()

  const handleNewBooking = () => {
    navigate('/schedule')
  }

  const handleQuickActions = () => {
    setShowQuickActions(!showQuickActions)
  }

  const quickActionItems = isTrainerMode ? [
    { icon: '📅', label: 'New Session', action: () => navigate('/schedule') },
    { icon: '👥', label: terminology.Clients || 'Clients', action: () => navigate('/clients') },
    { icon: '📄', label: 'Create Invoice', action: () => navigate('/invoices') },
    { icon: '📍', label: terminology.Venues || 'Venues', action: () => navigate('/venues') },
    { icon: '📦', label: 'Session Packages', action: () => navigate('/session-packages') },
  ] : [
    { icon: '📅', label: 'New Booking', action: () => navigate('/schedule') },
    { icon: '💰', label: 'Take Payment', action: () => navigate('/billing') },
    { icon: '📄', label: 'Create Invoice', action: () => navigate('/billing') },
    { icon: '👥', label: terminology.Members || 'Members', action: () => navigate('/clients') },
    { icon: '📊', label: 'View Reports', action: () => navigate('/reports') },
    { icon: '⚙️', label: 'Settings', action: () => navigate('/settings') },
  ]

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname
    switch (path) {
      case '/':
        return 'Dashboard'
      case '/schedule':
        return 'Schedule'
      case '/clients':
        return 'Clients'
      case '/members':
        return 'Clients'
      case '/billing':
        return 'Billing'
      case '/reports':
        return 'Reports'
      case '/settings':
        return 'Settings'
      case '/facilities':
        return 'Facilities'
      case '/venues':
        return 'Venues'
      case '/session-packages':
        return 'Session Packages'
      case '/staff':
        return 'Staff'
      case '/analytics':
        return 'Analytics'
      case '/checkin':
        return 'Check-in'
      case '/profile':
        return 'My Profile'
      default:
        return 'AI Facility Manager'
    }
  }

  const getUserInitials = () => {
    if (profile?.full_name) {
      return profile.full_name
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return user?.email?.slice(0, 2).toUpperCase() || 'U'
  }

  const getRoleBadgeColor = () => {
    if (isAdmin) return 'bg-red-500/20 text-red-400 border-red-400/30'
    if (isStaff) return 'bg-blue-500/20 text-blue-400 border-blue-400/30'
    return 'bg-green-500/20 text-green-400 border-green-400/30'
  }

  // Check if super admin is in platform mode
  const isInPlatformMode = isSuperAdmin && localStorage.getItem('super-admin-mode') === 'platform'

  return (
    <div className="top-bar" style={{ justifyContent: 'space-between' }}>
      {/* Left Side - Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        {/* Quick Action Buttons */}
        {(isAdmin || isStaff) && (
          <>
            <button 
              className="action-btn"
              onClick={handleNewBooking}
              style={{ 
                backgroundColor: themeColors.primary,
                color: themeColors.background
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColors.primaryHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = themeColors.primary;
              }}
            >
              <span>➕</span>
              New Booking
            </button>
            
            <button 
              className="action-btn"
              onClick={handleQuickActions}
              style={{ 
                border: `1px solid ${themeColors.primary}`,
                color: themeColors.text,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = themeColors.primary;
                e.currentTarget.style.color = themeColors.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = themeColors.text;
              }}
            >
              <span>⚡</span>
              Quick Actions
            </button>
          </>
        )}
        
        {/* Cart Button for Members */}
        {isMember && (
          <button 
            className="action-btn relative"
            onClick={() => setShowCart(true)}
            style={{ 
              border: `1px solid ${themeColors.primary}`,
              color: themeColors.text,
              backgroundColor: 'transparent',
              padding: '0.5rem 1rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = themeColors.primary;
              e.currentTarget.style.color = themeColors.background;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = themeColors.text;
            }}
          >
            <ShoppingCartIcon className="h-5 w-5 inline-block" />
            {itemCount > 0 && (
              <span 
                className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
                style={{ backgroundColor: themeColors.primary }}
              >
                {itemCount}
              </span>
            )}
            <span className="ml-2">Cart</span>
          </button>
        )}
      </div>

      {/* Right Side - User Profile and Organization */}
      <div className="top-bar-actions" style={{ gap: '1rem' }}>
        {/* Organization Badge - Removed as requested */}
        {/* <OrganizationBadge /> */}
        
        {/* Organization Switcher for Super Admins */}
        {isSuperAdmin && (
          <OrganizationSwitcher />
        )}

        {/* User Profile */}
        <div className="user-profile" onClick={() => setShowUserMenu(!showUserMenu)}>
          <div className="user-avatar">
            {getUserInitials()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
              {profile?.full_name || user?.email?.split('@')[0]}
            </div>
            <div style={{ fontSize: '0.8rem', color: themeColors.primaryLight }}>
              {isSuperAdmin ? 'SUPER ADMIN' : (organizationRole?.toUpperCase() || profile?.role?.toUpperCase() || 'MEMBER')}
            </div>
          </div>
        </div>

      </div>

      {/* User Dropdown Menu Portal */}
      {showUserMenu && createPortal(
        <>
          {/* Click-outside overlay */}
          <div 
            style={{ position: 'fixed', inset: 0, zIndex: 1001 }}
            onClick={() => setShowUserMenu(false)}
          />
          
          {/* Dropdown menu */}
          <div style={{
            position: 'fixed',
            right: '20px',
            top: '70px',
            width: '250px',
            background: 'rgba(0,0,0,0.95)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${themeColors.border}`,
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            zIndex: 1002,
            padding: '1rem'
          }}>
            <div style={{ borderBottom: `1px solid ${themeColors.borderLight}`, paddingBottom: '1rem', marginBottom: '1rem' }}>
              <div style={{ color: '#ffffff', fontWeight: '600', marginBottom: '0.5rem' }}>
                {profile?.full_name || 'User'}
              </div>
              <div style={{ color: themeColors.textSecondary, fontSize: '0.85rem' }}>
                {user?.email}
              </div>
              <div style={{ color: themeColors.primary, fontSize: '0.8rem', marginTop: '0.5rem' }}>
                Role: {isSuperAdmin ? 'SUPER ADMIN' : (organizationRole?.toUpperCase() || profile?.role?.toUpperCase() || 'MEMBER')}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button 
                onClick={() => navigate('/profile')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem',
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
                  cursor: 'pointer', borderRadius: '6px', fontSize: '0.9rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = themeColors.primaryLight
                  e.currentTarget.style.color = themeColors.primary
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'rgba(255,255,255,0.8)'
                }}
              >
                <span>👤</span>
                My Profile
              </button>
              
              <button 
                onClick={() => navigate('/settings')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem',
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
                  cursor: 'pointer', borderRadius: '6px', fontSize: '0.9rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = themeColors.primaryLight
                  e.currentTarget.style.color = themeColors.primary
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'rgba(255,255,255,0.8)'
                }}
              >
                <span>⚙️</span>
                Account Settings
              </button>
              
              <hr style={{ border: 'none', borderTop: `1px solid ${themeColors.border}`, margin: '0.5rem 0' }} />
              
              <button 
                onClick={signOut}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem',
                  background: 'none', border: 'none', color: '#F44336',
                  cursor: 'pointer', borderRadius: '6px', fontSize: '0.9rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(244, 67, 54, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span>🚪</span>
                Sign Out
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
      
      {/* Quick Actions Dropdown Portal */}
      {showQuickActions && createPortal(
        <>
          {/* Click-outside overlay */}
          <div 
            style={{ position: 'fixed', inset: 0, zIndex: 1001 }}
            onClick={() => setShowQuickActions(false)}
          />
          
          {/* Dropdown menu */}
          <div style={{
            position: 'fixed',
            right: '200px',
            top: '70px',
            width: '320px',
            background: '#1a1a1a',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            zIndex: 1002,
            padding: '1rem'
          }}>
            <div style={{ 
              borderBottom: '1px solid rgba(255,255,255,0.1)', 
              paddingBottom: '0.75rem', 
              marginBottom: '0.75rem' 
            }}>
              <div style={{ 
                color: '#f0f0f0', 
                fontWeight: '600', 
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>⚡</span>
                Quick Actions
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {quickActionItems.map((item, index) => (
                <button 
                  key={index}
                  onClick={() => {
                    item.action()
                    setShowQuickActions(false)
                  }}
                  style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.75rem', 
                    padding: '0.75rem 1rem',
                    background: 'transparent', 
                    border: '1px solid transparent', 
                    color: '#f0f0f0',
                    cursor: 'pointer', 
                    borderRadius: '8px', 
                    fontSize: '0.9rem',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    overflow: 'visible'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'
                    e.currentTarget.style.color = '#f5f5f5'
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'
                    e.currentTarget.style.transform = 'translateX(4px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.color = '#f0f0f0'
                    e.currentTarget.style.borderColor = 'transparent'
                    e.currentTarget.style.transform = 'translateX(0)'
                  }}
                >
                  <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ fontWeight: '500', overflow: 'visible', whiteSpace: 'nowrap' }}>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
      
      {/* Cart Sidebar for Members */}
      {showCart && <CartSidebar isOpen={showCart} onClose={() => setShowCart(false)} />}
    </div>
  )
}